import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';
import { loadPhotoIndex, photoFileNames, toPublicPhotoPath } from '../../data/photoFileNames';

const Home: React.FC = () => {
  // Wedding date: August 10, 2026
  const weddingDate = useMemo(() => new Date('2026-08-10T09:00:00'), []); // 9:00 AM ceremony
  
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isAfterWedding: false
  });

  // Random background images for countdown (3 images on each refresh)
  const [bgNames, setBgNames] = useState<string[]>([]);
  const [bgCount, setBgCount] = useState<number>(3);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const count = w < 640 ? 1 : w < 1024 ? 2 : 3; // 1 on narrow, 2 on small, 3 on large
      setBgCount(count);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    let mounted = true;
    const pick = (names: string[]) => {
      const shuffled = [...names].sort(() => Math.random() - 0.5);
      const chosen = shuffled.slice(0, Math.max(1, bgCount));
      if (mounted) setBgNames(chosen);
    };
    loadPhotoIndex().then((names) => {
      if (names.length > 0) pick(names);
      else pick(photoFileNames);
    });
    return () => { mounted = false; };
  }, [bgCount]);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const difference = weddingDate.getTime() - now.getTime();
      
      if (difference <= 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isAfterWedding: true
        });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({
        days,
        hours,
        minutes,
        seconds,
        isAfterWedding: false
      });
    };

    // Calculate initial time
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [weddingDate]);

  return (
    <div className="wedding-hero">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        {/* Full-bleed background collage inside the hero (big pink box) */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 h-full">
            {bgNames.map((name) => (
              <img
                key={name}
                src={toPublicPhotoPath(name)}
                alt="Wedding memory"
                loading="eager"
                className="w-full h-full object-cover"
              />
            ))}
          </div>
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-wedding mb-6 text-white">
              Natalie & Avarokin
            </h1>
            <p className="text-xl md:text-2xl text-white mb-8 max-w-2xl mx-auto">
              We're getting married and we want you to be there!
            </p>
            
            {/* Countdown with random photo background */}
            {!timeRemaining.isAfterWedding ? (
              <div className="relative max-w-5xl mx-auto mb-12">
                <div className="relative flex items-center justify-center p-4">
                  <div className="bg-white/90 backdrop-blur rounded-lg shadow-lg p-6 md:p-8 max-w-2xl w-full">
                    <h2 className="text-2xl font-wedding text-gray-900 mb-6 text-center">
                      Countdown to our Big Day
                    </h2>
                    <div className="grid grid-cols-4 gap-0 md:gap-1 mb-4 max-w-md mx-auto">
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold text-primary-600">
                          {timeRemaining.days}
                        </div>
                        <div className="text-sm text-gray-600">
                          {timeRemaining.days === 1 ? 'Day' : 'Days'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold text-primary-600">
                          {timeRemaining.hours}
                        </div>
                        <div className="text-sm text-gray-600">
                          {timeRemaining.hours === 1 ? 'Hour' : 'Hours'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold text-primary-600">
                          {timeRemaining.minutes}
                        </div>
                        <div className="text-sm text-gray-600">
                          {timeRemaining.minutes === 1 ? 'Minute' : 'Minutes'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold text-primary-600">
                          {timeRemaining.seconds}
                        </div>
                        <div className="text-sm text-gray-600">
                          {timeRemaining.seconds === 1 ? 'Second' : 'Seconds'}
                        </div>
                      </div>
                    </div>
                    <div className="text-lg text-gray-600 text-center">
                      until we say "I do"!
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto mb-12">
                <h2 className="text-2xl font-wedding text-gray-900 mb-4">
                  We're Married! 💍
                </h2>
                <p className="text-lg text-gray-600">
                  Thank you for celebrating with us!
                </p>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/rsvp" 
                className="wedding-button text-lg px-8 py-3"
              >
                RSVP Now
              </Link>
              <Link 
                to="/details" 
                className="wedding-button-outline text-lg px-8 py-3"
              >
                Details
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Date */}
            <div className="wedding-card text-center">
              <CalendarIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-wedding text-gray-900 mb-2">
                When
              </h3>
              <p className="text-gray-600">
                August 10, 2026
              </p>
            </div>

            {/* Location */}
            <div className="wedding-card text-center">
              <MapPinIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-wedding text-gray-900 mb-2">
                Where
              </h3>
              <p className="text-gray-600">
                Redfish Lake, Idaho
              </p>
            </div>

            {/* Timeline */}
            <div className="wedding-card text-center">
              <ClockIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-wedding text-gray-900 mb-2">
                Schedule
              </h3>
              <p className="text-gray-600">
                1:30 PM - Wedding ceremony
              </p>
              <p className="text-gray-600">
                Photos following the ceremony
              </p>
              <p className="text-gray-600">
                4:00 PM – 6:00 PM - Reception dinner
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Message */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-wedding text-gray-900 mb-6">
            You're Invited!
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            We are so excited to share our special day with the people we love most. 
            Your presence would mean the world to us as we begin this new chapter 
            of our lives together. Please join us for a celebration filled with 
            love, laughter, and unforgettable memories.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home; 